import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Chip, Stack, TableCell, TableRow, Typography } from '@mui/material';
import BasicTable, { NUMERIC_CELL_SX } from '../../../../components/BasicTable/BasicTable';
import RowActionsMenu from '../../../../components/RowActionsMenu/RowActionsMenu';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import GenericInfoModal from '../../../../components/GenericInfoModal';
import SchemeDrawer from './SchemeDrawer';
import { ReactComponent as AddIcon } from '../../../../assets/add-icon.svg';
import { ITableHeadings, ITableMetadata } from '../../../../common/interfaces/common.interface';
import { initialMetadataState, MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import { ActionMode, GeneralStatusEnum } from '../../../../common/enums/common.enum';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import { formatMoney } from '../../../../utilities/helper/money.helper';
import { BillingSchemeDto } from '../../../../common/interfaces/invoicing.interface';
import { deleteBillingScheme, getBillingSchemes, getBillingSettings } from '../../../../api/invoicing.api';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { palette } from '../../../../theme';
export const BILLING_SCHEMES_TABLE_HEADINGS: ITableHeadings[] = [
  { id: 1, value: '', label: 'billing.schemes.scheme', sortable: false, width: '22%' },
  { id: 2, value: '', label: 'billing.arrangement.items', sortable: false },
  { id: 3, value: '', label: 'billing.schemes.monthlyFixed', sortable: false, align: 'right' },
  { id: 4, value: '', label: 'common.groups', sortable: false, width: '28%' },
  { id: 5, value: '', label: 'common.status', sortable: false },
  { id: 6, value: '', label: '', sortable: false, width: 56 }
];

interface BillingSchemesTabProps {
  kindergartenId: string;
  /** After anything is saved or removed, so the setup cards above can recount. */
  onChanged?: () => void;
}

/**
 * Schemes decide what a group is billed for. A child inherits the scheme of its group,
 * so this is where recurring billing is actually configured.
 */
const BillingSchemesTab: React.FC<BillingSchemesTabProps> = ({ kindergartenId, onChanged }) => {
  const { t } = useTranslation();
  const [schemes, setSchemes] = useState<BillingSchemeDto[]>([]);
  const [loader, setLoader] = useState(false);
  const [metadata, setMetadata] = useState<ITableMetadata>(initialMetadataState);
  const [vatApplicable, setVatApplicable] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<BillingSchemeDto | null>(null);
  const [toDelete, setToDelete] = useState<BillingSchemeDto | null>(null);

  const loadSchemes = useCallback(async () => {
    setLoader(true);
    try {
      const result = await getBillingSchemes(kindergartenId);
      const items = result.results ?? [];
      setSchemes(items);
      // the endpoint returns everything at once, so paging is local
      setMetadata(prev => ({
        ...prev,
        totalItems: items.length,
        totalPages: Math.max(1, Math.ceil(items.length / prev.itemsPerPage))
      }));
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('billing.schemes.loadFailed'));
    } finally {
      setLoader(false);
    }
  }, [kindergartenId, t]);

  useEffect(() => {
    loadSchemes();
  }, [loadSchemes]);

  // `?schemeId=` opens that scheme: the contract panel links here to edit a group's price
  // list. The parameter is dropped once used, so closing the modal and reloading does not
  // open it again.
  const location = useLocation();
  const navigate = useNavigate();
  const linkedSchemeId = new URLSearchParams(location.search).get('schemeId');
  useEffect(() => {
    if (!linkedSchemeId || !schemes.length) return;
    const linked = schemes.find(scheme => scheme.id === linkedSchemeId);
    if (linked) setEditing(linked);
    const params = new URLSearchParams(location.search);
    params.delete('schemeId');
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [linkedSchemeId, schemes, location.pathname, location.search, navigate]);

  useEffect(() => {
    getBillingSettings(kindergartenId)
      .then(result => setVatApplicable(!!result.results?.isInVatSystem))
      .catch(() => undefined);
  }, [kindergartenId]);

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteBillingScheme(kindergartenId, toDelete.id);
      createNotification(MESSAGE_TYPE.SUCCESS, t('billing.schemes.removed'));
      setToDelete(null);
      loadSchemes();
      onChanged?.();
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('billing.schemes.removeFailed'));
    }
  };

  const from = (metadata.currentPage - 1) * metadata.itemsPerPage;
  const visible = schemes.slice(from, from + metadata.itemsPerPage);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 24, color: 'text.primary', fontFamily: 'Poppins' }}>{t('billing.schemes.title')}</Typography>
        <GenericButton
          buttonType={GenericButtonTypeEnum.Primary}
          variant="contained"
          startIcon={<AddIcon />}
          label={t('billing.schemes.create')}
          onClick={() => setCreating(true)}
        />
      </Stack>

      {!loader && !schemes.length && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          {t('billing.schemes.noneYet')}
        </Alert>
      )}

      <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 4 }}>
        <BasicTable headings={BILLING_SCHEMES_TABLE_HEADINGS} loader={loader} metadata={metadata} setMetadata={setMetadata}>
          {visible.map(scheme => (
            <TableRow
              key={scheme.id}
              hover
              tabIndex={0}
              sx={{ cursor: 'pointer' }}
              onClick={() => setEditing(scheme)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setEditing(scheme);
                }
              }}
            >
              <TableCell>
                {scheme.name}
                {scheme.description && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{scheme.description}</Typography>}
              </TableCell>
              <TableCell>{scheme.itemCount}</TableCell>
              <TableCell sx={NUMERIC_CELL_SX}>{formatMoney(scheme.monthlyFixedNet)}</TableCell>
              <TableCell>
                {scheme.groups.length ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {scheme.groups.map(group => (
                      <Chip key={group.id} size="small" variant="outlined" label={group.name} />
                    ))}
                  </Stack>
                ) : (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('billing.schemes.notAssigned')}</Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  variant="outlined"
                  label={t(`enums.generalStatus.${scheme.status}`)}
                  color={scheme.status === GeneralStatusEnum.ACTIVE ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell>
                <RowActionsMenu
                  name={scheme.name}
                  actions={[{ label: t('billing.schemes.remove'), danger: true, onClick: () => setToDelete(scheme) }]}
                />
              </TableCell>
            </TableRow>
          ))}
        </BasicTable>
      </Box>

      {creating && (
        <SchemeDrawer
          mode={ActionMode.CREATE}
          kindergartenId={kindergartenId}
          vatApplicable={vatApplicable}
          onClose={() => setCreating(false)}
          onSaved={() => {
            loadSchemes();
            onChanged?.();
          }}
        />
      )}
      {editing && (
        <SchemeDrawer
          mode={ActionMode.EDIT}
          kindergartenId={kindergartenId}
          vatApplicable={vatApplicable}
          scheme={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            loadSchemes();
            onChanged?.();
          }}
        />
      )}
      {toDelete && (
        <GenericInfoModal
          open
          onClose={() => setToDelete(null)}
          onConfirm={handleDelete}
          label={t('billing.schemes.remove')}
          message={t('billing.schemes.removeConfirm')}
          confirmLabel={t('common.remove')}
          entityName={toDelete.name}
        />
      )}
    </Box>
  );
};

export default BillingSchemesTab;
